import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import RefinementChips, { RefinementAxis, Refinements } from './RefinementChips'

export interface Citation {
  source: string
  url?: string
  title?: string
}

export type { RefinementAxis, Refinements }

export type FollowUpKey =
  | 'treatment'
  | 'non_pharm'
  | 'resources'
  | 'history'
  | 'exam'
  | 'billing'
  | 'referrals'

export const FOLLOW_UP_LABELS: Record<FollowUpKey, string> = {
  treatment: 'More on treatment',
  non_pharm: 'Non-pharm options',
  resources: 'Patient resources',
  history: 'History',
  exam: 'Exam',
  billing: 'Billing (PBS / MBS)',
  referrals: 'Referrals',
}

export interface ResponseData {
  answer: string
  citations: Citation[]
  pbsCodes?: string[]
  mbsItems?: string[]
  patientHandouts?: {
    title: string
    url: string
    source: 'healthdirect' | 'nps' | 'other'
  }[]
  followUps?: FollowUpKey[]
  refinementAxes?: RefinementAxis[]
  needsClarification?: boolean
  clarificationOptions?: string[]
  primarySource?: string
  queryId?: string
  // System-level error (auth, rate-limit-check, network) — renders as a notice
  // rather than an answer. Set by AppMain when /query returns non-2xx.
  error?: 'system' | 'expired' | 'unauthenticated' | 'cap_reached'
}

interface ResponsePanelProps {
  response?: ResponseData
  isLoading?: boolean
  mode?: 'clinic' | 'study'
  activeRefinements?: Refinements
  onFeedback?: (queryId: string, feedback: 'up' | 'down', text?: string) => void
  onFollowUp?: (key: FollowUpKey) => void
  onRefine?: (axisKey: string, option: string) => void
  onClearRefinement?: (axisKey: string) => void
  onClarify?: (option: string) => void
}

// Headers the model occasionally smuggles in despite the system prompt's
// "no Summary / Key Points / Recommendations / Clinical Response" rule.
// Strip them client-side as belt-and-braces so the pyramid format is preserved.
const SMUGGLED_HEADER_WORDS = [
  'Summary',
  'Key Points',
  'Recommendations',
  'Clinical Response',
  'Overview',
  'Bottom Line',
  'TL;DR',
  'Synthesis',
  'Answer',
]

function stripSmuggledHeaders(text: string): string {
  if (!text) return text
  const wordPattern = SMUGGLED_HEADER_WORDS
    .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')

  const patterns = [
    // Markdown ATX headers: "## Summary" / "### Key Points:"
    new RegExp(`^\\s*#{1,6}\\s+(?:${wordPattern})\\s*:?\\s*$`, 'gim'),
    // Bold-only heading line: "**Summary:**" / "**Key Points**"
    new RegExp(`^\\s*\\*\\*\\s*(?:${wordPattern})\\s*:?\\s*\\*\\*\\s*$`, 'gim'),
    // Plain heading line ending in colon: "Summary:" on its own line
    new RegExp(`^\\s*(?:${wordPattern})\\s*:\\s*$`, 'gim'),
  ]

  let out = text
  for (const p of patterns) out = out.replace(p, '')
  // Collapse the resulting blank-line runs.
  return out.replace(/\n{3,}/g, '\n\n').trim()
}

export default function ResponsePanel({
  response,
  isLoading = false,
  mode = 'clinic',
  activeRefinements = {},
  onFeedback,
  onFollowUp,
  onRefine,
  onClearRefinement,
  onClarify,
}: ResponsePanelProps) {
  const [feedbackState, setFeedbackState] = useState<'up' | 'down' | null>(null)
  const [feedbackText, setFeedbackText] = useState('')
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)

  const handleCopyAnswer = async () => {
    if (!response?.answer) return
    try {
      await navigator.clipboard.writeText(response.answer)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  const handleFeedbackSubmit = (type: 'up' | 'down') => {
    if (!response?.queryId) return
    if (type === feedbackState) {
      setFeedbackState(null)
      setShowFeedbackForm(false)
      setFeedbackText('')
      return
    }
    setFeedbackState(type)
    if (type === 'down') {
      setShowFeedbackForm(true)
    } else {
      onFeedback?.(response.queryId, type)
      setShowFeedbackForm(false)
      setFeedbackText('')
    }
  }

  const handleFeedbackTextSubmit = () => {
    if (!response?.queryId || !feedbackState) return
    onFeedback?.(response.queryId, feedbackState, feedbackText.trim() || undefined)
    setShowFeedbackForm(false)
    setFeedbackText('')
  }

  if (isLoading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8">
        <div className="animate-pulse space-y-4">
          <div className="flex items-center space-x-3 mb-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600 font-medium">Searching Australian sources...</span>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!response) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Ready</h3>
        <p className="text-gray-600">
          Ask anything about Australian clinical practice.
        </p>
      </div>
    )
  }

  // System error: render as a neutral notice, no source badge / chips / feedback.
  if (response.error) {
    const errStyles =
      response.error === 'cap_reached'
        ? { wrap: 'bg-amber-50 border-amber-200', title: 'text-amber-900', body: 'text-amber-800', label: 'Daily limit reached' }
        : response.error === 'unauthenticated'
        ? { wrap: 'bg-amber-50 border-amber-200', title: 'text-amber-900', body: 'text-amber-800', label: 'Sign-in needed' }
        : response.error === 'expired'
        ? { wrap: 'bg-amber-50 border-amber-200', title: 'text-amber-900', body: 'text-amber-800', label: 'Subscription' }
        : { wrap: 'bg-red-50 border-red-200', title: 'text-red-900', body: 'text-red-800', label: 'Service issue' }
    return (
      <div className={`border rounded-lg p-6 ${errStyles.wrap}`}>
        <h3 className={`text-sm font-semibold uppercase tracking-wide mb-2 ${errStyles.title}`}>
          {errStyles.label}
        </h3>
        <p className={`text-sm leading-relaxed ${errStyles.body}`}>{response.answer}</p>
      </div>
    )
  }

  // Clarifier: chip-only. No preamble, no question, no trailer.
  // Each chip re-issues the original query scoped to that option.
  if (response.needsClarification) {
    const options = response.clarificationOptions ?? []
    // Fall back to the answer text only if the model returned no options
    // (older payloads or sanitisation drop-through).
    if (options.length === 0 && response.answer) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">{response.answer}</p>
        </div>
      )
    }
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => onClarify?.(opt)}
              className="px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors border border-blue-200"
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    )
  }

  const cleanAnswer = stripSmuggledHeaders(response.answer)
  const showBilling = (response.pbsCodes?.length ?? 0) > 0 || (response.mbsItems?.length ?? 0) > 0
  const showHandouts = (response.patientHandouts?.length ?? 0) > 0
  const showCitations = response.citations.length > 0
  const followUps = response.followUps ?? []
  const refinementAxes = response.refinementAxes ?? []
  const showRefinements = onRefine !== undefined &&
    (refinementAxes.length > 0 || Object.keys(activeRefinements).length > 0)

  // Mode density: Clinic is tight (eyes-on under 3s); Study is relaxed.
  const density = mode === 'clinic'
    ? { padding: 'p-4', stack: 'space-y-3', proseSpacing: 'mb-2', leading: 'leading-snug' }
    : { padding: 'p-6', stack: 'space-y-5', proseSpacing: 'mb-3', leading: 'leading-relaxed' }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className={`${density.padding} ${density.stack}`}>
        {/* Primary source badge — reinforces attribution above the answer. */}
        {response.primarySource && (
          <div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
              <span className="text-gray-500 mr-1">According to</span>
              {response.primarySource}
            </span>
          </div>
        )}

        {/* Answer — pyramid, plain. h1/h2/h3 collapsed to <p> as a second
            line of defence against any header that slipped through. */}
        <div className="prose max-w-none text-left">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ children }) => <p className={`text-gray-900 ${density.leading} ${density.proseSpacing}`}>{children}</p>,
              h2: ({ children }) => <p className={`text-gray-900 ${density.leading} ${density.proseSpacing}`}>{children}</p>,
              h3: ({ children }) => <p className={`text-gray-900 ${density.leading} ${density.proseSpacing}`}>{children}</p>,
              p: ({ children }) => <p className={`text-gray-900 ${density.leading} ${density.proseSpacing}`}>{children}</p>,
              ul: ({ children }) => <ul className={`list-disc pl-5 text-gray-900 ${density.proseSpacing} space-y-1`}>{children}</ul>,
              ol: ({ children }) => <ol className={`list-decimal pl-5 text-gray-900 ${density.proseSpacing} space-y-1`}>{children}</ol>,
              li: ({ children }) => <li className="text-gray-900">{children}</li>,
              strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
              em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
              code: ({ children, className }) => {
                const isInline = !className
                return isInline ? (
                  <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">{children}</code>
                ) : (
                  <pre className="bg-gray-100 text-gray-800 p-3 rounded-lg overflow-x-auto"><code>{children}</code></pre>
                )
              },
            }}
          >
            {cleanAnswer}
          </ReactMarkdown>
        </div>

        {/* Refinement axes — narrow the answer along orthogonal dimensions */}
        {showRefinements && (
          <div className="pt-1">
            <RefinementChips
              axes={refinementAxes}
              active={activeRefinements}
              onSelect={(k, o) => onRefine?.(k, o)}
              onClear={(k) => onClearRefinement?.(k)}
            />
          </div>
        )}

        {/* Learn more — follow-up chips */}
        {followUps.length > 0 && (
          <div className="pt-1">
            <span className="text-xs uppercase tracking-wide text-gray-500 mr-3">Learn more:</span>
            <span className="inline-flex flex-wrap gap-2 align-middle">
              {followUps.map((key) => (
                <button
                  key={key}
                  onClick={() => onFollowUp?.(key)}
                  className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                >
                  {FOLLOW_UP_LABELS[key]}
                </button>
              ))}
            </span>
          </div>
        )}

        {/* Billing — only when explicitly returned */}
        {showBilling && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex flex-wrap gap-2">
              {response.pbsCodes?.map((code) => (
                <span key={`pbs-${code}`} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  PBS {code}
                </span>
              ))}
              {response.mbsItems?.map((item) => (
                <span key={`mbs-${item}`} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  MBS {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Patient handouts */}
        {showHandouts && (
          <div className="pt-2 border-t border-gray-100 space-y-2">
            {response.patientHandouts?.map((h, i) => (
              <a
                key={i}
                href={h.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700 hover:bg-blue-100 transition-colors mr-2"
              >
                {h.title}
                <span className="ml-2 text-xs text-blue-500">({h.source})</span>
              </a>
            ))}
          </div>
        )}

        {/* Citations */}
        {showCitations && (
          <div className="pt-2 border-t border-gray-100">
            <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">Sources</h4>
            <ol className="text-sm text-gray-700 space-y-1 list-decimal pl-5">
              {response.citations.map((c, i) => (
                <li key={i}>
                  <span className="font-medium text-gray-900">{c.source}</span>
                  {c.title && <span className="text-gray-600"> — {c.title}</span>}
                  {c.url && (
                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline">
                      view
                    </a>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Footer: copy + feedback */}
        <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleFeedbackSubmit('up')}
              className={`p-1.5 rounded-md transition-colors ${
                feedbackState === 'up' ? 'bg-green-100 text-green-600' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
              }`}
              aria-label="Helpful"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V9a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
            </button>
            <button
              onClick={() => handleFeedbackSubmit('down')}
              className={`p-1.5 rounded-md transition-colors ${
                feedbackState === 'down' ? 'bg-red-100 text-red-600' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
              }`}
              aria-label="Not helpful"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06L17 4m-7 10v2a2 2 0 002 2h.095c.5 0 .905-.405.905-.905 0-.714.211-1.412.608-2.006L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
              </svg>
            </button>
          </div>
          <button
            onClick={handleCopyAnswer}
            className={`text-xs font-medium px-2 py-1 rounded-md transition-colors ${
              copySuccess ? 'bg-green-100 text-green-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {copySuccess ? 'Copied' : 'Copy'}
          </button>
        </div>

        {showFeedbackForm && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="What could be improved?"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={2}
            />
            <div className="flex justify-end space-x-2 mt-2">
              <button
                onClick={() => { setShowFeedbackForm(false); setFeedbackText(''); setFeedbackState(null) }}
                className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleFeedbackTextSubmit}
                className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
